import { ART_TIER_COUNT, MAX_TIER, sortedOptionsFor, unlocksAtTier } from './layer-stack'
import type {
  BoOption,
  BoSection,
  GateIssue,
  LockedDisplay,
  PerTierEntry,
  RenderFit,
  SectionKind,
  TierRule,
  UpsertSectionInput,
  Visibility,
} from './types'

export const TIER_COUNT = ART_TIER_COUNT

export type LadderKind = 'none' | 'count' | 'slugs'

export interface SectionForm {
  id: string
  kind: SectionKind
  displayName: string
  ladderKind: LadderKind
  perTier: PerTierEntry[]
  perSlug: Record<string, number>
  swatches: string[]
  paintOrder: string
  backPaintOrder: string
  lockedDisplay: LockedDisplay | ''
  renderOpacity: string
  renderFit: RenderFit
  tints: Record<string, string>
  visibility: Visibility
}

export const emptyPerTier = (): PerTierEntry[] =>
  Array.from({ length: TIER_COUNT }, () => 0)

export function formFromSection(section?: BoSection): SectionForm {
  const rule = section?.tierRule ?? null

  return {
    id: section?.id ?? '',
    kind: section?.kind ?? 'option',
    displayName: section?.displayName ?? '',
    ladderKind: rule ? rule.kind : 'none',
    perTier:
      rule?.kind === 'count' ? normalisePerTier(rule.perTier) : emptyPerTier(),
    perSlug: rule?.kind === 'slugs' ? { ...rule.perSlug } : {},
    swatches: section?.swatches ? [...section.swatches] : [],
    paintOrder: numberField(section?.paintOrder),
    backPaintOrder: numberField(section?.backPaintOrder),
    lockedDisplay: section?.lockedDisplay ?? '',
    renderOpacity: numberField(section?.renderOpacity ?? 1),
    renderFit: section?.renderFit ?? 'fill',
    tints: { ...(section?.tints ?? {}) },
    visibility: { ...(section?.visibility ?? {}) },
  }
}

const numberField = (n: number | null | undefined): string =>
  n === null || n === undefined ? '' : String(n)

function normalisePerTier(perTier: PerTierEntry[]): PerTierEntry[] {
  const out = emptyPerTier()
  perTier.slice(0, TIER_COUNT).forEach((v, i) => {
    out[i] = v
  })

  return out
}

export function tierRuleFromForm(form: SectionForm): TierRule | null {
  if (form.ladderKind === 'count') {
    return { kind: 'count', perTier: normalisePerTier(form.perTier) }
  }
  if (form.ladderKind === 'slugs') {
    return { kind: 'slugs', perSlug: { ...form.perSlug } }
  }

  return null
}

/**
 * Every column the row has, always. `POST /v1/bo/categories` is a whole-row upsert, so a field
 * left out here is a field CLEARED in the database.
 */
export function toUpsertInput(
  setId: string,
  form: SectionForm,
): UpsertSectionInput {
  return {
    setId,
    id: form.id,
    kind: form.kind,
    displayName: form.displayName.trim() || null,
    paintOrder: optionalNumber(form.paintOrder),
    backPaintOrder: optionalNumber(form.backPaintOrder),
    visibility: form.visibility,
    lockedDisplay: form.lockedDisplay === '' ? null : form.lockedDisplay,
    tints: Object.keys(form.tints).length ? form.tints : null,
    renderOpacity: optionalNumber(form.renderOpacity),
    renderFit: form.renderFit,
    swatches: form.swatches.length ? form.swatches : null,
    tierRule: tierRuleFromForm(form),
  }
}

const optionalNumber = (raw: string): number | null => {
  const trimmed = raw.trim()
  if (trimmed === '') {
    return null
  }
  const n = Number(trimmed)

  return Number.isFinite(n) ? n : null
}

export interface TierRow {
  tier: number
  entry: PerTierEntry
  allowed: number
  newlyUnlocked: string[]
  unlocksNothing: boolean
}

export interface LadderReview {
  rows: TierRow[]
  unreachable: string[]
  total: number
}

export function reviewLadder(
  form: SectionForm,
  options: BoOption[],
  sectionId: string,
): LadderReview {
  const names =
    form.kind === 'color'
      ? form.swatches.map((s) => s)
      : sortedOptionsFor(sortedInput(options), sectionId).map((o) =>
          labelFor(options, o.slug),
        )
  const rule = tierRuleFromForm(form)
  const total = names.length

  if (!rule) {
    return {
      rows: [],
      unreachable: [],
      total,
    }
  }

  const reachable = new Set<number>()
  const rows: TierRow[] = []
  let seen = 0

  for (let tier = 0; tier < ART_TIER_COUNT; tier += 1) {
    const allowed = countAt(rule, tier, total)
    const newlyUnlocked = names.slice(seen, Math.max(seen, allowed))
    for (let i = 0; i < allowed && i < total; i += 1) {
      reachable.add(i)
    }
    rows.push({
      tier,
      entry: rule.kind === 'count' ? rule.perTier[tier] ?? 0 : allowed,
      allowed: Math.min(allowed, total),
      newlyUnlocked,
      unlocksNothing: newlyUnlocked.length === 0,
    })
    seen = Math.max(seen, Math.min(allowed, total))
  }

  const unreachable =
    rule.kind === 'slugs'
      ? names.filter(
          (_n, i) =>
            unlocksAtTier(rule, i, nameSlug(options, sectionId, i)) === null,
        )
      : names.filter((_n, i) => !reachable.has(i))

  return { rows, unreachable, total }
}

const sortedInput = (options: BoOption[]) => options

function countAt(rule: TierRule, tier: number, total: number): number {
  if (rule.kind === 'slugs') {
    return Object.values(rule.perSlug).filter((t) => t <= tier).length
  }
  const value = rule.perTier[tier]

  return value === 'all' ? total : typeof value === 'number' ? value : 0
}

const labelFor = (options: BoOption[], slug: string): string =>
  options.find((o) => o.slug === slug)?.displayName ?? slug

const nameSlug = (
  options: BoOption[],
  sectionId: string,
  index: number,
): string => sortedOptionsFor(options, sectionId)[index]?.slug ?? ''

export const SWATCH = /^[0-9A-Fa-f]{6}$/

export type SectionFieldErrors = Partial<
  Record<keyof SectionForm | 'form', string>
>

export function validateForm(form: SectionForm): SectionFieldErrors {
  const errors: SectionFieldErrors = {}

  if (!/^[A-Za-z0-9][A-Za-z0-9_-]*$/.test(form.id)) {
    errors.id = 'sectionId'
  }
  if (form.displayName.length > 120) {
    errors.displayName = 'tooLong'
  }
  if (form.ladderKind === 'count') {
    const bad = form.perTier.some(
      (v) => v !== 'all' && (!Number.isInteger(v) || (v as number) < 0),
    )
    if (bad || form.perTier.length !== TIER_COUNT) {
      errors.perTier = 'perTier'
    }
  }
  if (form.ladderKind === 'slugs') {
    const bad = Object.values(form.perSlug).some(
      (t) => !Number.isInteger(t) || t < 0 || t > MAX_TIER,
    )
    if (bad) {
      errors.perSlug = 'perSlug'
    }
  }
  if (form.swatches.some((s) => !SWATCH.test(s))) {
    errors.swatches = 'swatch'
  }
  const opacity = optionalNumber(form.renderOpacity)
  if (opacity !== null && (opacity <= 0 || opacity > 1)) {
    errors.renderOpacity = 'opacity'
  }

  return errors
}

/**
 * `validateForm` returns CODES; the API returns prose. Both land in the same field, so a code is
 * translated and anything else is shown as it came.
 */
export const SECTION_ERROR_MESSAGE_IDS: Readonly<Record<string, string>> = {
  sectionId: 'avatarStudio.editor.idInvalid',
  tooLong: 'avatarStudio.editor.error.nameTooLong',
  perTier: 'avatarStudio.editor.error.perTier',
  perSlug: 'avatarStudio.editor.error.perSlug',
  swatch: 'avatarStudio.editor.error.swatch',
  opacity: 'avatarStudio.editor.error.opacity',
}

export function sectionErrorText(
  code: string | undefined,
  translate: (id: string) => string,
): string | undefined {
  if (!code) {
    return undefined
  }
  const id = SECTION_ERROR_MESSAGE_IDS[code]

  return id ? translate(id) : code
}

type IssueRoute = [RegExp, keyof SectionForm]

const ISSUE_TO_FIELD: IssueRoute[] = [
  [/tierRule|perTier|perSlug/i, 'perTier'],
  [/swatch/i, 'swatches'],
  [/renderOpacity|opacity/i, 'renderOpacity'],
  [/renderFit|fit/i, 'renderFit'],
  [/paintOrder/i, 'paintOrder'],
  [/displayName/i, 'displayName'],
  [/tints/i, 'tints'],
]

/** The API's field errors, aimed at the control that produced them. */
export function fieldErrorsFromIssues(
  issues: GateIssue[],
): SectionFieldErrors {
  const errors: SectionFieldErrors = {}
  issues.forEach((issue) => {
    const text = `${issue.code} ${issue.detail ?? ''}`
    const hit = ISSUE_TO_FIELD.find(([pattern]) => pattern.test(text))
    // `??` keeps an EMPTY detail, which rendered a banner with no text in it.
    const message = issue.detail?.trim() || issue.code
    if (hit) {
      errors[hit[1]] = message
    } else {
      errors.form = message
    }
  })

  return errors
}

export interface PerSlugRow {
  key: string
  label: string
  /** Always a tier: an unmentioned slug is ungated, which is tier 0, never "no tier". */
  tier: number
}

/**
 * One row per thing the section can offer, with the tier it is assigned. `perSlug` is keyed by
 * option slug for a picture section and by hex for a colour one, which is what the API stores.
 */
function perSlugKeys(
  form: SectionForm,
  options: BoOption[],
  sectionId: string,
): string[] {
  return form.kind === 'color'
    ? form.swatches
    : sortedOptionsFor(options, sectionId).map((o) => o.slug)
}

/**
 * The tier each thing EFFECTIVELY has, so a section still stored as a count ladder opens with its
 * rows filled in rather than every row reading "Never".
 */
export function perSlugRows(
  form: SectionForm,
  options: BoOption[],
  sectionId: string,
): PerSlugRow[] {
  const effective = seedPerSlug(form, options, sectionId)

  return perSlugKeys(form, options, sectionId).map((key) => ({
    key,
    label: form.kind === 'color' ? key : labelFor(options, key),
    tier: effective[key] ?? 0,
  }))
}

export function withPerSlug(
  perSlug: Record<string, number>,
  key: string,
  tier: number,
): Record<string, number> {
  return { ...perSlug, [key]: tier }
}

/**
 * The per-thing map the CURRENT rule already implies, so switching from "By count" to "Per option"
 * starts from what is live rather than from nothing. Switching used to empty the map, which read
 * on screen as "10 of 10 can never be picked".
 */
export function seedPerSlug(
  form: SectionForm,
  options: BoOption[],
  sectionId: string,
): Record<string, number> {
  if (Object.keys(form.perSlug).length > 0) {
    return { ...form.perSlug }
  }
  const rule = tierRuleFromForm(form)
  const seeded: Record<string, number> = {}
  perSlugKeys(form, options, sectionId).forEach((key, index) => {
    const at = rule ? unlocksAtTier(rule, index, key) : 0
    if (at !== null) {
      seeded[key] = at
    }
  })

  return seeded
}

/** Every row at once. */
export function setAllPerSlug(
  form: SectionForm,
  options: BoOption[],
  sectionId: string,
  tier: number,
): Record<string, number> {
  return perSlugKeys(form, options, sectionId).reduce<Record<string, number>>(
    (acc, key) => ({ ...acc, [key]: tier }),
    {},
  )
}
